import { useCallback, useMemo, useState } from "react";

import {
  Button,
  Icon_BottomBar24,
  Icon_ChevronUpDown24,
  Icon_Kebab,
  Menu,
  useBottomSheetDrag,
  useManagedModal,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";

import { webAppURL } from "../../config";
import useNavigation from "../../hooks/navigation";
import useAccount from "../../hooks/useAccount";
import { useProject } from "../../hooks/useProject";
import { useProjectOrWebExport } from "../../hooks/useProjectOrWebExport";
import { useMutateProjectViewLink } from "../../hooks/useProjectViewLink";
import { useTeamActiveSubscription } from "../../hooks/useTeamActiveSubscription";
import { useTracker } from "../../hooks/useTracker";
import { useWebExport } from "../../hooks/useWebExport";
import { ProjectRoleUtils, SubscriptionPeriod } from "../../types";
import { handleFileDownload } from "../../utils/handleFileDownload";
import { formatDate } from "../../utils/helpers";
import { DeepLinkModal } from "../DeepLinkModal/DeepLinkModal";
import { OverlayProvider } from "../Headers/components/ShareModal/components/MenuPortal";
import { PaywallModal } from "../Headers/components/ShareModal/components/PaywallModal";
import { RenameVersionModal } from "../RenameVersionModal/RenameVersionModal";
import { Toolbar } from "../Toolbar/Toolbar";
import { ToolButton } from "../ToolButton/ToolButton";
import { UnpublishConfirmationModal } from "../UnpublishConfirmationModal/UnpublishConfirmationModal";
import { ProjectMetadata } from "./ProjectMetadata/ProjectMetadata";

import style from "./ProjectSidebarMobile.module.css";

interface ProjectSidebarMobileProps {
  isOpen: boolean;
  setOpen?: (open: boolean) => void;
  isCommentingEnabled?: boolean;
}

export const ProjectSidebarMobile = ({
  isOpen,
  setOpen,
  isCommentingEnabled,
}: ProjectSidebarMobileProps) => {
  const { data: accountData } = useAccount();
  const { data: projectData, isLoading: isProjectLoading } = useProject();
  const { data: webExportData, viewLinkId } = useWebExport();
  const { data: projectOrWebExportData } = useProjectOrWebExport();
  const { projectId = "", drawingId = "" } = useParams();

  const navigation = useNavigation();

  const [isUnpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const { open: openShareModal } = useManagedModal({ id: "SHARE_VIEW_LINK" });
  const renameVersionModal = useManagedModal();

  const userName = useMemo(() => {
    const user = projectData?.users?.find(
      (user) => user.userId === projectData.latestRevision.uploadedByUserId
    );
    if (user && user.userId === accountData?.id) {
      return user.name ? `${user.name}` : "You";
    }
    return user?.name;
  }, [projectData, accountData]);

  const tracker = useTracker();

  const { data: hasTeamSubscription } = useTeamActiveSubscription(projectData?.teamId);

  const [isOwnerPaywallOpen, setIsOwnerPaywallOpen] = useState(false);
  const [isNonOwnerPaywallOpen, setIsNonOwnerPaywallOpen] = useState(false);

  const handleCopyLink = () => {
    const url = `${webAppURL}/p/${webExportData?.webExport.projectId}/v/${
      projectData?.projectViewLinks?.[0]?.projectViewLinkId ?? ""
    }`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast.success("Copied to clipboard");
      })
      .catch(() => {
        toast.error("Failed to copy to clipboard");
      });
  };

  const shouldShowDownloadButton =
    projectData?.latestRevision.fileDownload?.url && projectData.project.projectId;

  const paywallReturnURL = useMemo(() => window.location.href, []);

  const handleDownloadClick = useCallback(() => {
    if (!projectData) return;

    tracker.trackDownloadFileClicked({
      projectTeamId: projectData.teamId,
      projectId: projectData.project.projectId,
    });

    if (hasTeamSubscription) {
      handleFileDownload(projectData?.latestRevision.fileDownload?.url);
      return;
    }

    const isOwner = ProjectRoleUtils.isAtLeast(projectData.userRole, "owner");
    if (isOwner) {
      setIsOwnerPaywallOpen(true);
    } else {
      setIsNonOwnerPaywallOpen(true);
    }

    tracker.trackPaywallModalShown({
      remoteProjectId: projectData.project.projectId,
      paywallReason: "download_shapr_file",
      hasSubscribeAction: isOwner,
    });
  }, [tracker, projectData, hasTeamSubscription]);

  const canShare =
    projectOrWebExportData?.userRole &&
    ProjectRoleUtils.isAtLeast(projectOrWebExportData.userRole, "editor");

  const handleMetadataMenuOpen = () => {
    tracker.trackContextMenuOpened({
      location: "project_page_project_title",
    });
  };

  const handleMetadataCopyLink = () => {
    tracker.trackContextMenuActed({
      action: "copy_link",
      location: "project_page_project_title",
    });
    handleCopyLink();
  };

  const handleMetadataShare = () => {
    tracker.trackContextMenuActed({
      action: "share",
      location: "project_page_project_title",
    });
    openShareModal();
  };

  const handleMetadataDownload = () => {
    tracker.trackContextMenuActed({
      action: "download",
      location: "project_page_project_title",
    });
    handleDownloadClick();
  };

  const {
    mutateAsync: mutateProjectViewLink,
    isPending: isMutateProjectViewLinkPending,
    isError: isMutateProjectViewLinkError,
  } = useMutateProjectViewLink();

  const handleRenameVersion = async (versionName: string) => {
    try {
      await mutateProjectViewLink({
        viewLinkId: viewLinkId ?? "",
        body: {
          versionName: versionName,
        },
      });
      renameVersionModal.close();
    } catch {
      // Error handling is done via mutateProjectViewLinkError
    }
  };

  const { sheetRef, handleProps } = useBottomSheetDrag({
    onClose: () => setOpen?.(false),
  });

  return (
    <>
      <div className={style.controlButton}>
        <ToolButton
          alt="Project Outline"
          title="Project Outline"
          icon={<Icon_BottomBar24 />}
          onClick={() => {
            const newState = isOpen ? "close" : "open";
            tracker.trackProjectSidebarToggled({ newState });
            setOpen?.(!isOpen);
          }}
          isActive={isOpen}
          hideShadow
        />
      </div>
      {!drawingId && isCommentingEnabled && (
        <div className={style.toolbarWrapper}>
          <Toolbar />
        </div>
      )}

      <div ref={sheetRef} className={classNames(style.bottomSheet, { [style.isOpen]: isOpen })}>
        <div
          className={style.handle}
          onClick={() => {
            const newState = isOpen ? "close" : "open";
            tracker.trackProjectSidebarToggled({ newState });
            setOpen?.(!isOpen);
          }}
          {...handleProps}
        >
          <div className={style.handleBar} />
        </div>

        <div className={style.content}>
          {projectOrWebExportData && (
            <>
              <ProjectMetadata
                isLoading={isProjectLoading}
                teamId={projectOrWebExportData.teamId}
                spaceName={projectOrWebExportData.spaceName}
                projectName={projectOrWebExportData.projectName}
                lastModifiedAt={projectOrWebExportData.lastModifiedAt}
                userName={projectOrWebExportData.userName}
                isCurrentUser={projectOrWebExportData.isCurrentUser}
                showEditInAppButton={ProjectRoleUtils.isAtLeast(
                  projectOrWebExportData.userRole,
                  "editor"
                )}
                onMenuOpen={handleMetadataMenuOpen}
                onCopyLink={handleMetadataCopyLink}
                onShare={handleMetadataShare}
                onDownload={handleMetadataDownload}
                canShare={canShare}
                canDownload={Boolean(shouldShowDownloadButton)}
              />
              <div className={style.divider}></div>
            </>
          )}

          {webExportData && (
            <OverlayProvider>
              <div className={style.versionsWrapper}>
                <div className={style.currentVersion}>
                  <div className={style.icon}>
                    <Button variant="borderless" disabled>
                      <Icon_ChevronUpDown24 style={{ height: "16px", width: "16px" }} />
                    </Button>
                  </div>
                  <div className={style.data}>
                    <div className={style.title}>
                      {webExportData.versionName ?? formatDate(webExportData?.webExport.createdAt)}
                    </div>
                    {projectData && <div className={style.subtitle}>Published by {userName}</div>}
                    <div className={style.subtitle}>
                      #<span>{webExportData.webExport.revisionId}</span>
                    </div>
                  </div>
                  {projectData && (
                    <div className={style.actions}>
                      <Menu
                        activator={
                          <Button variant="borderless" aria-label="More options">
                            <Icon_Kebab />
                          </Button>
                        }
                        className={style.contextMenu}
                        onOpen={() => {
                          tracker.trackContextMenuOpened({
                            location: "project_page_version_selector",
                          });
                        }}
                      >
                        {projectOrWebExportData?.userRole &&
                          ProjectRoleUtils.isAtLeast(projectOrWebExportData.userRole, "editor") && (
                            <Menu.Item
                              closeOnClick
                              onClick={() => {
                                tracker.trackContextMenuActed({
                                  action: "rename",
                                  location: "project_page_version_selector",
                                });
                                renameVersionModal.open();
                              }}
                            >
                              <span>Rename</span>
                            </Menu.Item>
                          )}

                        <Menu.Item
                          closeOnClick
                          onClick={() => {
                            tracker.trackContextMenuActed({
                              action: "copy_link",
                              location: "project_page_version_selector",
                            });
                            handleCopyLink();
                          }}
                        >
                          <span>Copy Link</span>
                        </Menu.Item>

                        {projectOrWebExportData?.userRole &&
                          ProjectRoleUtils.isAtLeast(projectOrWebExportData.userRole, "editor") && (
                            <>
                              <Menu.Item
                                closeOnClick
                                onClick={() => {
                                  tracker.trackContextMenuActed({
                                    action: "share",
                                    location: "project_page_version_selector",
                                  });
                                  openShareModal();
                                }}
                              >
                                <span>Share...</span>
                              </Menu.Item>

                              <Menu.Item
                                closeOnClick
                                onClick={() => {
                                  tracker.trackContextMenuActed({
                                    action: "unpublish",
                                    location: "project_page_version_selector",
                                  });
                                  setUnpublishModalOpen(true);
                                }}
                              >
                                <span style={{ color: "var(--red-500)" }}>Unpublish</span>
                              </Menu.Item>
                            </>
                          )}
                      </Menu>
                    </div>
                  )}
                </div>

                <div className={style.modelList}>
                  <button
                    className={classNames([style.listItem, !drawingId && style.active])}
                    onClick={() => {
                      navigation.goToViewLink({
                        viewLinkId: viewLinkId ?? "",
                        projectId,
                      });
                      setOpen?.(false);
                    }}
                  >
                    {projectOrWebExportData?.thumbnailUrl && (
                      <div className={style.listItemIcon}>
                        <img
                          src={projectOrWebExportData?.thumbnailUrl}
                          alt="Model Thumbnail"
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                      </div>
                    )}

                    <div>Model</div>
                  </button>
                </div>
                {webExportData.webExport.drawingsDownload?.length && (
                  <div className={style.drawingsList}>
                    <div className={style.drawingsTitle}>Drawings</div>

                    {webExportData.webExport.drawingsDownload.map((drawing) => (
                      <button
                        key={drawing.drawingId}
                        className={classNames([
                          style.listItem,
                          drawing.drawingId === drawingId && style.active,
                        ])}
                        onClick={() => {
                          navigation.goToDrawing({
                            viewLinkId: viewLinkId ?? "",
                            projectId,
                            drawingId: drawing.drawingId,
                          });
                          setOpen?.(false);
                        }}
                      >
                        <div>{drawing.title}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </OverlayProvider>
          )}
          {!webExportData && projectData && (
            <div className={style.versionsWrapper}>
              <div className={style.currentVersion}>
                <div className={style.noVersions}>No version published yet</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <RenameVersionModal
        modalProps={renameVersionModal.props}
        versionName={webExportData?.versionName}
        onSave={handleRenameVersion}
        isPending={isMutateProjectViewLinkPending}
        isError={isMutateProjectViewLinkError}
      />
      <UnpublishConfirmationModal
        open={isUnpublishModalOpen}
        onClose={() => setUnpublishModalOpen(false)}
        onUnpublishSuccess={() => {
          navigation.goToProject({ projectId: projectData?.project.projectId ?? "" });
        }}
        viewLinkId={viewLinkId ?? ""}
        telemetryLocation={"project_page"}
      />
      <DeepLinkModal />
      <PaywallModal
        modalProps={{ open: isOwnerPaywallOpen, onClose: () => setIsOwnerPaywallOpen(false) }}
        title={"Subscribe to export Shapr3D"}
        description={"Get instant access to all features without limits."}
        showSubscriptionButton={true}
        onClose={() => {
          setIsOwnerPaywallOpen(false);
          if (projectData) {
            tracker.trackPaywallModalDismissed({
              remoteProjectId: projectData.project.projectId,
              paywallReason: "download_shapr_file",
            });
          }
        }}
        onClick={() => {
          if (projectData) {
            navigation.goToCheckout({
              period: SubscriptionPeriod.Yearly,
              showPeriodPicker: true,
              utmSource: "download_shapr_file",
              returnToURL: paywallReturnURL,
            });
            tracker.trackPaywallSubscribeClicked({
              paywallReason: "download_shapr_file",
              remoteProjectId: projectData.project.projectId,
            });
          }
        }}
      />
      <PaywallModal
        modalProps={{
          open: isNonOwnerPaywallOpen,
          onClose: () => setIsNonOwnerPaywallOpen(false),
        }}
        title={"Your current team plan doesn't include this feature."}
        size="medium"
        description={
          "Contact your team owner to upgrade the subscription and gain access to all Pro features."
        }
        onClose={() => {
          setIsNonOwnerPaywallOpen(false);
          if (projectData) {
            tracker.trackPaywallModalDismissed({
              remoteProjectId: projectData.project.projectId,
              paywallReason: "download_shapr_file",
            });
          }
        }}
        showSubscriptionButton={false}
      />
    </>
  );
};
